import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { ISO_COUNTRY_NAMES } from '../utils/countryNames.js'
import { localizeSalesperson } from '../utils/localizedField.js'

const DEFAULT_COUNTRIES = Object.keys(ISO_COUNTRY_NAMES)
  .sort((a, b) => ISO_COUNTRY_NAMES[a].localeCompare(ISO_COUNTRY_NAMES[b]))
  .map(code => ({ value: code, label: ISO_COUNTRY_NAMES[code] }))

export default ({ appId, searchKey, indexEnv, brandSlug, locale }) => {
  const client = algoliasearch(appId, searchKey)

  return {
    zip: '',
    country: 'US',
    countries: DEFAULT_COUNTRIES,
    loading: false,
    searched: false,
    results: null,

    async init() {
      try {
        const result = await client.search({
          requests: [{
            indexName: `${indexEnv}_salespeople`,
            query: '',
            filters: `brand_slugs:"${brandSlug}"`,
            hitsPerPage: 0,
            facets: ['country_iso_codes'],
          }],
        })
        const facet = result.results?.[0]?.facets?.country_iso_codes ?? {}
        const codes = Object.keys(facet)
        const dynamic = codes
          .filter(code => ISO_COUNTRY_NAMES[code])
          .sort((a, b) => ISO_COUNTRY_NAMES[a].localeCompare(ISO_COUNTRY_NAMES[b]))
          .map(code => ({ value: code, label: ISO_COUNTRY_NAMES[code] }))

        if (dynamic.length) {
          this.country = dynamic.find(c => c.value === 'US')?.value ?? dynamic[0].value
          this.countries = dynamic
        }
      } catch {
        // keep DEFAULT_COUNTRIES
      }
    },

    async search() {
      const trimmed = this.zip.replace(/[^a-zA-Z0-9]/g, '').trim()
      if (!trimmed) return

      this.loading = true
      this.searched = false
      this.results = null

      const searchPostalCode = async (prefixedKey) => {
        const result = await client.search({
          requests: [{
            indexName: `${indexEnv}_zip_lookup`,
            query: '',
            filters: `postal_codes:"${prefixedKey}"`,
            hitsPerPage: 100,
            attributesToRetrieve: ['type', 'territory_id', 'region_code', 'country_code'],
          }],
        })
        return result.results?.[0]?.hits ?? []
      }

      try {
        // Step 1: Resolve ZIP/postal code to geography record(s).
        // Multiple hits means the code spans multiple territories.
        let hits

        // When selector is hidden (country defaults to US), auto-detect Canadian postal codes
        // by their letter-digit-letter prefix (e.g. V6B, K8N). Safe to scope to US-only: if
        // the selector is visible and user chose a different country, respect their selection.
        const effectiveCountry = (this.country === 'US' && /^[a-zA-Z]\d[a-zA-Z]/i.test(trimmed))
          ? 'CA'
          : this.country

        if (effectiveCountry === 'CA') {
          // Try 3-char FSA first (e.g. "K8N"), fall back to single letter (e.g. "K")
          const compact = trimmed.toUpperCase()
          hits = compact.length >= 3
            ? await searchPostalCode(`CA:${compact.substring(0, 3)}`)
            : []
          if (hits.length === 0) {
            hits = await searchPostalCode(`CA:${compact.charAt(0)}`)
          }
        } else {
          hits = await searchPostalCode(`${effectiveCountry}:${trimmed}`)
        }

        if (!hits.length) {
          this.results = { type: 'notfound' }
          return
        }

        // Step 2: Get territory team or individual salespeople
        const first = hits[0]
        let spFilter

        if (first.type === 'territory') {
          const territoryIds = hits.map(h => h.territory_id)
          const tFilter = territoryIds.map(id => `territory_id:${id}`).join(' OR ')
          spFilter = `(${tFilter}) AND record_type:territory`
        } else {
          const facetField = first.country_code === 'CA' ? 'province_codes' : 'state_codes'
          spFilter = `${facetField}:"${first.region_code}" AND brand_slugs:"${brandSlug}"`
        }

        const spResponse = await client.search({
          requests: [{
            indexName: `${indexEnv}_salespeople`,
            query: '',
            filters: spFilter,
            hitsPerPage: 100,
          }],
        })

        const spHits = spResponse.results?.[0]?.hits ?? []

        if (!spHits.length) {
          this.results = { type: 'notfound' }
        } else if (spHits[0].record_type === 'territory') {
          this.results = { type: 'territory', data: spHits[0] }
        } else {
          this.results = {
            type: 'salespeople',
            data: spHits.map(person => localizeSalesperson(person, locale)),
          }
        }
      } catch {
        this.results = { type: 'error' }
      } finally {
        this.loading = false
        this.searched = true
      }
    },

    toTelHref(phone) {
      if (!phone) return '#'
      const clean = phone.replace(/[^\d+]/g, '')
      return 'tel:' + (clean.startsWith('+') ? clean : '+1' + clean)
    },
  }
}
